import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Picker } from 'emoji-mart';

import { getThumbBySize } from '../../../util/photo';
import { IMAGE_SIZE_CODE } from '../../../constants/image-size-codes';
import { EMOJI_LIB_ENDPOINT } from '../../../constants/pro-custom-prints';
import PlusCircleIcon from '../../icons/PlusCircleIcon';
import { sanitizeEmojiPickerCodepointForDisplay } from './helpers';
import PinchZoomPan from '../PinchZoomPan';
import Button from '../Button';
import useComponentSize from '../../../hooks/useComponentSize';
import TwemojiLicense from './TwemojiLicense';

const propTypes = {
  className: PropTypes.string,
  onPhotoSelect: PropTypes.func.isRequired,
  onPhotoCropboxSet: PropTypes.func.isRequired,
  emoji: PropTypes.shape({
    id: PropTypes.string,
    unified: PropTypes.string,
  }),
  onEmojiSet: PropTypes.func.isRequired,
  photo: PropTypes.shape({
    id: PropTypes.string.isRequired,
    thumbnail_urls: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
      }),
    ),
  }),
};

const WaldogramSecondaryPhotoPlaceholder = ({
  onPhotoSelect,
  onPhotoCropboxSet,
  photo,
  emoji,
  className,
  onEmojiSet,
}) => {
  const photoWrapRef = useRef(null);
  const containerSize = useComponentSize(photoWrapRef);
  const [emojiPickerExpanded, setEmojiPickerExpanded] = useState(false);
  const baseClassName = 'waldogram-secondary-photo-select';
  const classes = classNames({
    [baseClassName]: true,
    [className]: !!className,
  });

  const handlePhotoSelect = event => {
    event.preventDefault();

    onPhotoSelect({
      position: 1,
      photo,
    });
  };

  const handlePhotoCropboxSet = cropbox => {
    onPhotoCropboxSet(1, cropbox);
  };

  return (
    <div className={classes} ref={photoWrapRef}>
      {!photo ? (
        <a
          href="#"
          onClick={handlePhotoSelect}
          className={`${baseClassName}-trigger`}
        >
          <span className={`${baseClassName}-placeholder`}>
            Select photo&hellip;
          </span>
        </a>
      ) : (
        <div className={`${baseClassName}-photo-wrap`} key={photo.id}>
          <PinchZoomPan
            key={photo.id}
            maxScale={10}
            initialRotation={0}
            initialLeft={0}
            initialTop={0}
            initialScale={1}
            onChange={({ x, y, scale, rotation }) => {
              handlePhotoCropboxSet({
                x: x / containerSize.width,
                y: y / containerSize.height,
                scale,
                rotation,
              });
            }}
            controlButtons={false}
          >
            <img
              src={
                getThumbBySize(photo.thumbnail_urls, IMAGE_SIZE_CODE.LARGE).url
              }
              className={`${baseClassName}-photo-img`}
              alt=""
            />
          </PinchZoomPan>
          <div className="waldogram-edit-buttons-centered">
            <Button
              className="btn-xs btn-edit-photo-selection"
              onClick={handlePhotoSelect}
            >
              Change
            </Button>
          </div>
        </div>
      )}

      <div className="waldogram-emoji-wrap">
        <button
          className="waldogram-emoji-trigger a"
          onClick={() => setEmojiPickerExpanded(show => !show)}
        >
          {emoji ? (
            <span>
              <img
                src={`${EMOJI_LIB_ENDPOINT}/${sanitizeEmojiPickerCodepointForDisplay(
                  emoji.unified,
                )}.svg`}
                alt=""
              />
            </span>
          ) : (
            <span className="waldogram-emoji-trigger-select">
              <img src={`${EMOJI_LIB_ENDPOINT}/1f600.svg`} alt="" />
              <PlusCircleIcon color="#93b7e6" size="14" />
            </span>
          )}
        </button>

        <div
          className="waldogram-emoji-picker-wrap"
          style={{
            display: emojiPickerExpanded ? 'block' : 'none',
          }}
        >
          <Picker
            set="twitter"
            showSkinTones={false}
            sheetSize={32}
            perLine={8}
            emojiSize={22}
            title="Pick an Emoji"
            emoji="point_up"
            recent={[
              'smile',
              'sunglasses',
              'hugging_face',
              'blush',
              'star-struck',
              'heart_eyes',
              'joy',
              'sweat_smile',
              'stuck_out_tongue_winking_eye',
              'kissing_heart',
              'kissing_closed_eyes',
              'heart',
              'two_hearts',
              'see_no_evil',
              'v',
              'thumbsup',
              'wave',
              'raised_hands',
              'the_horns',
              'muscle',
              'tada',
              'sunny',
              'tent',
              'mailbox_with_mail',
            ]}
            onSelect={selectedEmoji => {
              onEmojiSet(selectedEmoji);
              setEmojiPickerExpanded(false);
            }}
          />
          <TwemojiLicense className="text-white text-faded text-xs text-center mt-1" />
        </div>
      </div>
    </div>
  );
};

WaldogramSecondaryPhotoPlaceholder.propTypes = propTypes;

export default WaldogramSecondaryPhotoPlaceholder;
